import {
  AGENTIC_LOGIC_MAX_MAX_ITEMS,
  AGENTIC_LOGIC_MIN_MAX_ITEMS,
  type AgenticLogicRuleSettings,
} from "@intelligems/ig-types";
import {
  IgCheckbox,
  IgNumericalCounter,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";

export interface RuleSettingsFieldsProps {
  settings: AgenticLogicRuleSettings;
  saving: boolean;
  /** Replaces the settings WHOLE — see the note on partial objects below. */
  onChange: (settings: AgenticLogicRuleSettings) => void;
}

/**
 * The rule's recommendation settings: how many products it returns, which
 * products it refuses to return, and what the slot does when it returns none.
 *
 * These used to live on each container the rule fills — the checkout block's
 * `variables`, the onsite component's `config`, the placement's `onEmpty` — so
 * one rule could behave three ways and a merchant asking "why is this product
 * not showing" had to check every location. They are the rule's now, and the
 * selection-logic canvas draws them: the exclusions as filter cards, the item
 * cap on the UP TO card, the policy on the fallback terminal.
 *
 * A chat-authored rule is first asked these four questions in the conversation
 * that authors everything else about it (the survey card); a template rule is
 * asked on its create panel. Once the rule exists, both edit them here, on the
 * Display settings tab of the rule page.
 *
 * EVERY EDIT REPLACES THE OBJECT WHOLE, and takes effect at the next publish.
 * Serving reads the copy frozen onto the active version, so a merchant who
 * changes a setting here and expects the storefront to move needs to publish —
 * which is the same contract a template-params edit already has.
 */
export const RuleSettingsFields = ({
  settings,
  saving,
  onChange,
}: RuleSettingsFieldsProps) => {
  const update = (change: Partial<AgenticLogicRuleSettings>) =>
    onChange({ ...settings, ...change });

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }} alignItems="flex-start">
      <IgNumericalCounter
        label="Products returned"
        tooltipMessage="How many products the rule returns at most. The placement decides how many of them it shows at once."
        value={settings.maxItems}
        min={AGENTIC_LOGIC_MIN_MAX_ITEMS}
        max={AGENTIC_LOGIC_MAX_MAX_ITEMS}
        disabled={saving}
        onChange={(maxItems) => maxItems != null && update({ maxItems })}
      />

      <IgSelect
        label="If the rule returns no products"
        value={settings.onEmpty}
        disabled={saving}
        onChange={(event) =>
          update({
            onEmpty: event.target.value as AgenticLogicRuleSettings["onEmpty"],
          })
        }
        sx={{ width: 320 }}
      >
        <MenuItem value="hide">Hide the placement</MenuItem>
        <MenuItem value="smart-recs">Show smart recommendations</MenuItem>
      </IgSelect>

      <Stack>
        <FormControlLabel
          control={
            <IgCheckbox
              checked={settings.excludeOutOfStock}
              disabled={saving}
              onChange={(event) =>
                update({ excludeOutOfStock: event.target.checked })
              }
            />
          }
          label={
            <IgTypography variant="body2">
              Exclude out of stock products
            </IgTypography>
          }
        />
        <FormControlLabel
          control={
            <IgCheckbox
              checked={settings.excludeItemsInCart}
              disabled={saving}
              onChange={(event) =>
                update({ excludeItemsInCart: event.target.checked })
              }
            />
          }
          label={
            <IgTypography variant="body2">
              Exclude products already in the cart
            </IgTypography>
          }
        />
      </Stack>
    </Stack>
  );
};
